import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Music, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  Upload, Trash2, Repeat, Shuffle, Users, Lock, Info,
} from 'lucide-react';
import { useUserData } from '../contexts/UserDataContext';
import './MusicPlayer.css';

const formatTime = (sec) => {
  if (!Number.isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const MusicPlayer = () => {
  const { audioTracks, uploadAudioTrack, updateAudioTrack, deleteAudioTrack, audioTrackUrl } = useUserData();
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.6);
  const [muted, setMuted] = useState(false);
  const [loop, setLoop] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const currentTrack = audioTracks[currentIdx] || null;

  // Apply volume / mute to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  // Load track when index changes
  useEffect(() => {
    if (!audioRef.current || !currentTrack) return;
    const wasPlaying = isPlaying;
    audioRef.current.src = audioTrackUrl(currentTrack.id);
    audioRef.current.load();
    if (wasPlaying) {
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.id]);

  // If the current track was deleted, snap index back into range
  useEffect(() => {
    if (currentIdx >= audioTracks.length) {
      setCurrentIdx(Math.max(0, audioTracks.length - 1));
    }
  }, [audioTracks, currentIdx]);

  const handlePlayPause = async () => {
    if (!audioRef.current || !currentTrack) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (e) {
        setError('Playback blocked — click again or allow autoplay.');
      }
    }
  };

  const handleNext = () => {
    if (audioTracks.length === 0) return;
    if (shuffle) {
      let next;
      do {
        next = Math.floor(Math.random() * audioTracks.length);
      } while (next === currentIdx && audioTracks.length > 1);
      setCurrentIdx(next);
    } else {
      setCurrentIdx((i) => (i + 1) % audioTracks.length);
    }
  };

  const handlePrev = () => {
    if (audioTracks.length === 0) return;
    setCurrentIdx((i) => (i - 1 + audioTracks.length) % audioTracks.length);
  };

  const handleEnded = () => {
    if (loop && audioTracks.length === 1) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      return;
    }
    if (loop || currentIdx < audioTracks.length - 1) {
      handleNext();
    } else {
      setIsPlaying(false);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  // Get audio duration from file before uploading
  const probeDuration = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const probe = new Audio();
      probe.preload = 'metadata';
      probe.src = url;
      probe.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(probe.duration || null);
      };
      probe.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
    });

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    if (file.size > 12 * 1024 * 1024) {
      setError('Track must be under 12 MB');
      return;
    }
    const name = file.name.replace(/\.[^.]+$/, '');
    setUploading(true);
    try {
      const durationSec = await probeDuration(file);
      const created = await uploadAudioTrack(file, name, 'private', durationSec);
      const newIdx = audioTracks.length;
      setCurrentIdx(newIdx);
      void created;
    } catch (err) {
      setError('Upload failed. Check format (MP3/WAV/OGG/M4A) and size.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentTrack || currentTrack.is_mine === false) return;
    const ok = window.confirm(`Delete track "${currentTrack.name}"?`);
    if (!ok) return;
    try {
      const wasPlaying = isPlaying;
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      await deleteAudioTrack(currentTrack.id);
      if (wasPlaying && audioTracks.length > 1) {
        setIsPlaying(true);
      }
    } catch (e) {
      setError('Delete failed');
    }
  };

  const handleToggleVisibility = async () => {
    if (!currentTrack || currentTrack.is_mine === false) return;
    const next = currentTrack.visibility === 'team' ? 'private' : 'team';
    try {
      await updateAudioTrack(currentTrack.id, { visibility: next });
    } catch (e) {
      setError('Update failed');
    }
  };

  const progressPct = duration > 0 ? (progress / duration) * 100 : 0;

  const canEdit = currentTrack && currentTrack.is_mine !== false;

  const trackList = useMemo(() => audioTracks, [audioTracks]);

  return (
    <Card className="music-player bg-gray-900/60 border-gray-800 overflow-hidden" data-testid="music-player">
      <div className="music-header">
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4 text-teal-400" />
          <Label className="text-sm font-medium text-gray-200">Presentation Music</Label>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-gray-400 hover:text-teal-200 hover:bg-teal-950/40"
          onClick={() => setExpanded((v) => !v)}
          data-testid="toggle-music-tips"
        >
          <Info className="w-3.5 h-3.5 mr-1" /> Tips
        </Button>
      </div>

      {expanded && (
        <div className="music-tips">
          <p>
            <span className="text-teal-300 font-medium">To share with Zoom / Teams:</span> when starting your screen share,
            enable <span className="text-gray-200">"Share computer sound"</span>. Audio plays from this window, so keep it open during the meeting.
          </p>
          <p>
            <span className="text-teal-300 font-medium">Royalty-free sources:</span>{' '}
            <a href="https://pixabay.com/music/" target="_blank" rel="noreferrer">Pixabay</a>{', '}
            <a href="https://mixkit.co/free-stock-music/" target="_blank" rel="noreferrer">Mixkit</a>{', '}
            <a href="https://www.bensound.com/royalty-free-music" target="_blank" rel="noreferrer">Bensound</a>{', '}
            <a href="https://uppbeat.io/browse/music/corporate" target="_blank" rel="noreferrer">Uppbeat</a>.
            Download MP3 / WAV (under 12 MB) and upload here.
          </p>
        </div>
      )}

      {/* Now playing */}
      <div className="music-now-playing">
        {currentTrack ? (
          <>
            <div className="music-track-name" title={currentTrack.name}>
              {currentTrack.name}
              {currentTrack.visibility === 'team' && (
                <span className="music-badge music-badge-team">
                  <Users className="w-2.5 h-2.5" /> TEAM
                </span>
              )}
            </div>
            <div className="music-track-owner">
              {currentTrack.is_mine ? 'Your upload' : `Shared by ${currentTrack.owner_name || 'teammate'}`}
            </div>
          </>
        ) : (
          <div className="music-empty">
            <Music className="w-7 h-7 text-gray-600 mb-2" />
            <div className="text-gray-300 text-sm">No tracks yet</div>
            <div className="text-gray-500 text-xs">Upload an MP3 to begin</div>
          </div>
        )}
      </div>

      {/* Progress */}
      {currentTrack && (
        <div className="music-progress-row">
          <span className="music-time">{formatTime(progress)}</span>
          <div className="music-progress" onClick={handleSeek} data-testid="music-progress-bar">
            <div className="music-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="music-time">{formatTime(duration || currentTrack.duration_sec || 0)}</span>
        </div>
      )}

      {/* Transport controls */}
      <div className="music-controls">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShuffle((v) => !v)}
          className={`music-toggle-btn ${shuffle ? 'music-toggle-on' : ''}`}
          title="Shuffle"
          data-testid="music-shuffle"
        >
          <Shuffle className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handlePrev}
          disabled={trackList.length < 2}
          className="music-ctrl-btn"
          title="Previous"
          data-testid="music-prev"
        >
          <SkipBack className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          onClick={handlePlayPause}
          disabled={!currentTrack}
          className="music-play-btn"
          data-testid="music-play"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleNext}
          disabled={trackList.length < 2}
          className="music-ctrl-btn"
          title="Next"
          data-testid="music-next"
        >
          <SkipForward className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setLoop((v) => !v)}
          className={`music-toggle-btn ${loop ? 'music-toggle-on' : ''}`}
          title="Loop"
          data-testid="music-loop"
        >
          <Repeat className="w-4 h-4" />
        </Button>
      </div>

      {/* Volume */}
      <div className="music-volume-row">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setMuted((v) => !v)}
          className="music-ctrl-btn"
          data-testid="music-mute"
        >
          {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </Button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            setVolume(v);
            if (v > 0) setMuted(false);
          }}
          className="music-volume-slider"
          data-testid="music-volume"
        />
        <span className="music-volume-pct">{Math.round((muted ? 0 : volume) * 100)}%</span>
      </div>

      {/* Track management */}
      <div className="music-manage">
        {currentTrack && canEdit && (
          <div className="music-share-row">
            <div className="flex items-center gap-1.5">
              {currentTrack.visibility === 'team'
                ? <Users className="w-3 h-3 text-teal-400" />
                : <Lock className="w-3 h-3 text-gray-500" />}
              <span className="text-[11px] text-gray-300">
                {currentTrack.visibility === 'team' ? 'Shared with team' : 'Private to you'}
              </span>
            </div>
            <Switch
              checked={currentTrack.visibility === 'team'}
              onCheckedChange={handleToggleVisibility}
              data-testid="music-visibility-toggle"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-teal-800 bg-teal-950/40 text-teal-200 hover:bg-teal-900/50 hover:text-teal-100 text-xs h-8"
            data-testid="upload-music-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {uploading ? 'Uploading…' : 'Upload Track'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            disabled={!canEdit}
            className="border-red-900/60 bg-red-950/30 text-red-200 hover:bg-red-900/40 hover:text-red-100 text-xs h-8 disabled:opacity-40"
            title={!currentTrack ? 'No track selected' : (!canEdit ? 'Only the owner can delete' : 'Delete this track')}
            data-testid="delete-music-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/x-m4a,audio/aac"
          onChange={handleFileChosen}
          className="hidden"
          data-testid="music-file-input"
        />

        {error && <p className="text-[11px] text-red-300 mt-1">{error}</p>}
      </div>

      {/* Track list */}
      {trackList.length > 0 && (
        <div className="music-tracklist" data-testid="music-tracklist">
          {trackList.map((tr, idx) => (
            <button
              key={tr.id}
              type="button"
              onClick={() => setCurrentIdx(idx)}
              className={`music-track-row ${idx === currentIdx ? 'music-track-row-active' : ''}`}
              data-testid={`music-track-${tr.id}`}
            >
              <div className="music-track-row-icon">
                {idx === currentIdx && isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              </div>
              <span className="music-track-row-name">{tr.name}</span>
              {tr.visibility === 'team' && <Users className="w-3 h-3 text-teal-400" />}
              {tr.duration_sec ? (
                <span className="music-track-row-dur">{formatTime(tr.duration_sec)}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={() => setError('Could not load track')}
        preload="metadata"
      />
    </Card>
  );
};

export default MusicPlayer;
